/* Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect } from "react";
import { ListLoader, Text, theme } from "@nordcloud/gnui";
import {
  ReservationsAndCommitmentsCoverageOfferingType,
  ReservationsAndCommitmentsCoverageServiceUsageType,
  ReservationsAndCommitmentsFilters,
  ReservationsAndCommitmentsLookbackPeriod,
  ReservationsProvider,
  ReservationsRecommendationPaymentOption,
  ReservationsRecommendationTerm,
} from "~/generated/graphql";
import { Pagination, UniversalWrap } from "~/components";
import { useQueryState } from "~/hooks";

import { useGetCoverageData } from "../../hooks";
import { UsageCoverageData } from "../../types";
import { ReservationsCoverageTable } from "./ReservationsCoverageTable";

type CoverageTableProps = {
  provider: ReservationsProvider;
  timePeriod: ReservationsAndCommitmentsLookbackPeriod;
  offeringType: ReservationsAndCommitmentsCoverageOfferingType;
  serviceUsageType: ReservationsAndCommitmentsCoverageServiceUsageType;
  paymentOption: ReservationsRecommendationPaymentOption;
  termOption: ReservationsRecommendationTerm;
  filters?: ReservationsAndCommitmentsFilters;
  hasGroupByParameter: boolean;
  openSidebar: (rowDetails: UsageCoverageData) => void;
};

export function CoverageTable({
  filters,
  timePeriod,
  offeringType,
  serviceUsageType,
  paymentOption,
  termOption,
  provider,
  hasGroupByParameter,
  openSidebar,
}: CoverageTableProps) {
  const {
    state: { page, limit },
    updateQueryState,
  } = useQueryState<CoverageTableQueryState>();

  const {
    RICoverageGroupedData,
    RICoverageDataCount,
    RICoverageLoading,
    RICoverageError,
  } = useGetCoverageData({
    limit,
    page,
    offeringType,
    serviceUsageType,
    paymentOption,
    termOption,
    timePeriod,
    filters,
    provider,
  });

  useEffect(() => {
    if (page !== 0) {
      updateQueryState({ page: 0 });
    }
    // We don't want to put `page` and `updateQueryState` here, because it might block user from accessing other pages than 0
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceUsageType, termOption, paymentOption, timePeriod, offeringType]);

  return (
    <UniversalWrap
      loaderProps={{ loading: RICoverageLoading, Component: ListLoader }}
      errorProps={{ error: RICoverageError }}
    >
      <Text weight="medium" mb={theme.spacing.spacing03}>
        Usage Details
      </Text>
      <ReservationsCoverageTable
        resourcesData={RICoverageGroupedData ?? []}
        serviceType={serviceUsageType}
        offeringType={offeringType}
        timePeriod={timePeriod}
        filters={filters}
        provider={provider}
        hasGroupByParameter={hasGroupByParameter}
        openSidebar={openSidebar}
      />
      <Pagination count={RICoverageDataCount ?? 0} />
    </UniversalWrap>
  );
}

type CoverageTableQueryState = {
  offeringType?: ReservationsAndCommitmentsCoverageOfferingType;
  serviceUsageType?: ReservationsAndCommitmentsCoverageServiceUsageType;
  timePeriod?: ReservationsAndCommitmentsLookbackPeriod;
};
